import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import {
    LocalizationProvider,
    DatePicker,
    TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { format } from "date-fns";
import HelperFunctions from "../../shared utils/Utils";
import { SERVER_URL } from "../../../constants";
import { useAirports } from "../../Transfers/hooks/useAirports";

function AddTransfer({
    direction,
    passedStudent,
    transferDate,
    addTransfer,
    campuses = [],
    open: openProp,
    onClose: onCloseProp,
    hideTrigger = false,
    onSaved,
    isAdult = false,
    adultId,
}) {
  const [openInternal, setOpenInternal] = useState(false);

    const currentHour = new Date();
    currentHour.setMinutes(0, 0, 0);

    const { airports } = useAirports({ activeOnly: true });

    const makeEmptyTransfer = () => ({
        direction: direction || "IN",
        transferDate: transferDate || currentHour,
        depart: "",
        arrive: "",
        isAirportTransfer: true,
        privatePickup: false,
        departureTime: currentHour,
        arrivalTime: currentHour,
        flightId: "",
        unaccompaniedMinor: false,
        notes: "",
        internalTransfer: false,
    });

    const [transfer, setTransfer] = useState(makeEmptyTransfer);
    const [stays, setStays] = useState([]);

    const isControlled = typeof openProp === "boolean";
    const open = isControlled ? openProp : openInternal;

    useEffect(() => {
        if(!open) return;
        setTransfer(makeEmptyTransfer());

    }, [open, direction, passedStudent, transferDate]);

    // Load the person's stays so we can sanity-check the flight date against the
    // start/end of their stay at save time. Adults use NSRs (different endpoint
    // and lowercase field names) rather than student stays.
    useEffect(() => {
        const checkId = isAdult ? adultId : passedStudent;
        if (!open || !checkId) return;
        const token = sessionStorage.getItem("bearer");
        const url = isAdult
            ? `${SERVER_URL}api/nsrs/${checkId}`
            : `${SERVER_URL}api/student/${checkId}/stays`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => {
                const list = Array.isArray(data) ? data : [];
                setStays(
                    isAdult
                        ? list.map((n) => ({
                              arrivalDate: n.arrivaldate,
                              departureDate: n.departuredate,
                          }))
                        : list
                );
            })
            .catch(() => setStays([]));
    }, [open, passedStudent, adultId, isAdult]);

    const handleClickOpen = () => {
        if (!isControlled) setOpenInternal(true);
    };

    const handleClose = () => {
        onCloseProp?.();
        if (!isControlled) setOpenInternal(false);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (type === "checkbox") {
            setTransfer((prev) => {
                const updated = { ...prev, [name]: checked };

                if (name === "privatePickup") {
                    updated.flightId = checked ? "SELFTRANSFER" : "";
                }

                // Auto-enable self transfer if internal transfer is selected
                if (name === "internalTransfer" && checked) {
                    updated.privatePickup = true;
                    updated.flightId = "SELFTRANSFER";
                }

                return updated;
            });
        } else {
            setTransfer((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleDateChange = (date, name) => {
        setTransfer((prev) => ({ ...prev, [name]: date || new Date() }));
    };

    const handleSave = async () => {
        if (!transfer.transferDate) {
            alert("Transfer date is required.");
            return;
        }

        if (!transfer.privatePickup) {
            const valid =
                transfer.flightId === "PENDING" ||
                HelperFunctions.validateFlight(
                    transfer,
                    setTransfer,
                    /^([A-Z]{3}|[A-Z][A-Z0-9]|[0-9][A-Z])\d{1,4}$/
                );
            if (!valid) return;
        }

        // Warn (don't block) when an airport transfer's date isn't on the
        // student's stay start/end — the usual cause of a student vanishing from
        // transfer lists. Internal (campus-to-campus) transfers are exempt.
        if (!transfer.internalTransfer) {
            const dateCheck = HelperFunctions.checkTransferDateAgainstStays(transfer, stays);
            if (!dateCheck.onBoundary) {
                const word = dateCheck.direction === "OUT" ? "departure" : "arrival";
                const expectedStr = dateCheck.expected.length
                    ? dateCheck.expected.join(" or ")
                    : "—";
                const proceed = window.confirm(
                    `This ${word} flight is dated ${dateCheck.target}, but the ${word} ` +
                    `date of the stay is ${expectedStr}.\n\nTransfers dated away from the start/end of the stay ` +
                    `usually don't show on the right transfer list. Save anyway?`
                );
                if (!proceed) return;
            }
        }

        const effectiveFlightId = transfer.privatePickup ? "SELFTRANSFER" : transfer.flightId;

        const normalised = { ...transfer };

        // UI-only field should not go to backend
        delete normalised.isAirportTransfer;

        // Apply mapping rules only when not internalTransfer
        if (!transfer.internalTransfer) {
            const isAirport = !!transfer.isAirportTransfer;
            const dir = transfer.direction;

            if (isAirport) {
                // IN airport: arrive = airport code, depart = free text
                // OUT airport: depart = airport code, arrive = free text
                // -> nothing to null unless you want to ensure blanks become null
                if (dir === "IN") {
                    normalised.arrive = transfer.arrive || null; // airport code
                    normalised.depart = transfer.depart || null; // free text
                } else {
                    normalised.depart = transfer.depart || null; // airport code
                    normalised.arrive = transfer.arrive || null; // free text
                }
            } else {
                // Non-airport transfers:
                if (dir === "IN") {
                    // arrive = pickup point free text; depart = null
                    normalised.depart = null;
                    normalised.arrive = transfer.arrive || null;
                } else {
                    // depart = dropoff point free text; arrive = null
                    normalised.arrive = null;
                    normalised.depart = transfer.depart || null;
                }
            }
        }

        const payload = {
            ...normalised,
            flightId: effectiveFlightId,
            transferDate: transfer.transferDate
                ? format(transfer.transferDate, "yyyy-MM-dd")
                : null,
            departureTime: transfer.departureTime
                ? format(transfer.departureTime, "HH:mm:ss")
                : null,
            arrivalTime: transfer.arrivalTime
                ? format(transfer.arrivalTime, "HH:mm:ss")
                : null,
        };

        await addTransfer(payload, passedStudent);

        if (transfer.flightId && transfer.flightId !== "SELFTRANSFER") {
            try {
                const token = sessionStorage.getItem("bearer");
                const flightDate = payload.transferDate;
                await fetch(
                    `${SERVER_URL}api/flights/singleFlight` +
                        `?flightID=${encodeURIComponent(transfer.flightId)}` +
                        `&flightDate=${encodeURIComponent(flightDate)}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
            } catch (err) {
                console.error("Failed to upsert flight:", err);
            }
        }

        await Promise.resolve(onSaved?.(payload, passedStudent));

        handleClose();
    };

    return (
        <div>
            {!hideTrigger && (
                <IconButton color="primary" onClick={handleClickOpen}>
                    <AddIcon />
                </IconButton>
            )}            
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Add Transfer</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                        <Stack spacing={2} mt={1}>
                            <FormControl>
                                <InputLabel id="direction-label">
                                    Direction
                                </InputLabel>
                                <Select
                                    labelId="direction-label"
                                    id="direction"
                                    name="direction"
                                    value={transfer.direction}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="IN">IN</MenuItem>
                                    <MenuItem value="OUT">OUT</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="internalTransfer"
                                        checked={
                                            transfer.internalTransfer || false
                                        }
                                        onChange={handleChange}
                                    />
                                }
                                label="Internal Transfer (campus to campus)"
                            />
                            {!transfer.internalTransfer && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="isAirportTransfer"
                                            checked={!!transfer.isAirportTransfer}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Airport transfer"
                                />
                            )}
                            <DatePicker
                                label="Date"
                                value={transfer.transferDate}
                                onChange={(date) =>
                                    handleDateChange(date, "transferDate")
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        value={params.inputProps.value || ""}
                                    />
                                )}
                            />
                            {transfer.internalTransfer ? (
                                <FormControl fullWidth>
                                    <InputLabel id="from-campus-label">From Campus</InputLabel>
                                    <Select
                                        labelId="from-campus-label"
                                        name="depart"
                                        value={transfer.depart}
                                        onChange={handleChange}
                                    >
                                        {campuses.map((c) => (
                                            <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : transfer.isAirportTransfer ? (
                                // Airport transfer
                                transfer.direction === "OUT" ? (
                                    <FormControl fullWidth>
                                        <InputLabel id="depart-airport-label">Departure airport (served)</InputLabel>
                                        <Select
                                            labelId="depart-airport-label"
                                            name="depart"
                                            value={transfer.depart || ""}
                                            onChange={handleChange}
                                            label="Departure airport (served)"
                                        >
                                            <MenuItem value=""><em>Select airport</em></MenuItem>
                                            {airports.map((a) => (
                                                <MenuItem key={a.code} value={a.code}>
                                                    {a.code} — {a.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        label="Depart (free text)"
                                        name="depart"
                                        variant="standard"
                                        value={transfer.depart}
                                        onChange={handleChange}
                                        helperText="Optional: origin info"
                                    />
                                )
                            ) : (
                                // Non-airport transfer
                                transfer.direction === "OUT" ? (
                                    <TextField
                                        label="Drop-off point (free text)"
                                        name="depart"
                                        variant="standard"
                                        value={transfer.depart}
                                        onChange={handleChange}
                                        placeholder="e.g. Edinburgh city centre hotel"
                                    />
                                ) : (
                                    // IN non-airport: depart should be null, so hide it (or show disabled)
                                    <TextField
                                        label="Depart"
                                        name="depart"
                                        variant="standard"
                                        value=""
                                        disabled
                                        helperText="Not used for inbound non-airport transfers"
                                    />
                                )
                            )}

                            {transfer.internalTransfer ? (
                                <FormControl fullWidth>
                                    <InputLabel id="to-campus-label">To Campus</InputLabel>
                                    <Select
                                        labelId="to-campus-label"
                                        name="arrive"
                                        value={transfer.arrive}
                                        onChange={handleChange}
                                    >
                                        {campuses.map((c) => (
                                            <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : transfer.isAirportTransfer ? (
                                // Airport transfer
                                transfer.direction === "IN" ? (
                                    <FormControl fullWidth>
                                        <InputLabel id="arrive-airport-label">Arrival airport (served)</InputLabel>
                                        <Select
                                            labelId="arrive-airport-label"
                                            name="arrive"
                                            value={transfer.arrive || ""}
                                            onChange={handleChange}
                                            label="Arrival airport (served)"
                                        >
                                            <MenuItem value=""><em>Select airport</em></MenuItem>
                                                {airports.map((a) => (
                                                    <MenuItem key={a.code} value={a.code}>
                                                        {a.code} — {a.name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        label="Arrive (free text)"
                                        name="arrive"
                                        variant="standard"
                                        value={transfer.arrive}
                                        onChange={handleChange}
                                        helperText="Optional: destination info"
                                    />
                                )
                            ) : (
                                // Non-airport transfer
                                transfer.direction === "IN" ? (
                                    <TextField
                                        label="Pick-up point (free text)"
                                        name="arrive"
                                        variant="standard"
                                        value={transfer.arrive}
                                        onChange={handleChange}
                                        placeholder="e.g. Edinburgh city centre hotel"
                                    />
                                ) : (
                                    // OUT non-airport: arrive should be null
                                    <TextField
                                        label="Arrive"
                                        name="arrive"
                                        variant="standard"
                                        value=""
                                        disabled
                                        helperText="Not used for outbound non-airport transfers"
                                    />
                                )
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="privatePickup"
                                        checked={transfer.privatePickup}
                                        onChange={handleChange}
                                    />
                                }
                                label="Self Transfer"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="unaccompaniedMinor"
                                        checked={transfer.unaccompaniedMinor}
                                        onChange={handleChange}
                                    />
                                }
                                label="Unaccompanied Minor"
                            />
                            <TimePicker
                                label="Departure Time"
                                value={transfer.departureTime}
                                onChange={(time) =>
                                    handleDateChange(time, "departureTime")
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        value={params.inputProps.value || ""}
                                    />
                                )}
                            />
                            <TimePicker
                                label="Arrival Time"
                                value={transfer.arrivalTime}
                                onChange={(time) =>
                                    handleDateChange(time, "arrivalTime")
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        value={params.inputProps.value || ""}
                                    />
                                )}
                            />
                            <TextField
                                label="Flight Number"
                                name="flightId"
                                variant="standard"
                                value={transfer.flightId}
                                onChange={handleChange}
                                disabled={transfer.privatePickup}
                            />
                            <TextField
                                label="Notes"
                                name="notes"
                                variant="standard"
                                value={transfer.notes}
                                onChange={handleChange}
                                multiline
                                minRows={2}
                            />
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default AddTransfer;
